
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, TableBody, IconButton, Tooltip, Button } from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import EditIcon from '@mui/icons-material/Edit';
import {
  MainContainer,
  HeaderContainer,
  PageTitle,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  CreateButton
} from './styles';
import { useBreadcrumb } from '../../../../context/BreadcrumbContext';
import { useSidebar } from '../../../../context/SidebarContext';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../store';
import { fetchCustomForms, publishCustomForm } from '../../../../modules/masterdata/actions';
import {
  selectCustomForms,
  selectCustomFormSuccess,
  selectCustomFormError,
  selectPublishCustomFormLoading,
  selectPublishCustomFormSuccess,
  selectPublishCustomFormError
} from '../../../../modules/masterdata/selectors';
import { clearCustomFormState, resetPublishCustomFormState } from '../../../../modules/masterdata/reducers';
import { CustomForm } from '../../../../modules/masterdata/types';
import CreateCustomFormModal from './components/CreateCustomFormModal';
import AlertPopup from '../../../../components/common/AlertPopup';
import ConfirmationDialog from '../../../../components/common/ConfirmationDialog';

const CustomFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  const dispatch = useDispatch<AppDispatch>();

  const forms = useSelector(selectCustomForms);
  const operationSuccess = useSelector(selectCustomFormSuccess);
  const operationError = useSelector(selectCustomFormError);
  const publishLoading = useSelector(selectPublishCustomFormLoading);
  const publishSuccess = useSelector(selectPublishCustomFormSuccess);
  const publishError = useSelector(selectPublishCustomFormError);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [formToEdit, setFormToEdit] = useState<CustomForm | null>(null);
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [formToPublish, setFormToPublish] = useState<{ id: number; status: boolean } | null>(null);

  useEffect(() => {
    setActivePage('custom-form');
    setPath([
      { title: 'Settings', path: '/master-data' },
      { title: 'Master Data', path: '/master-data' },
      { title: 'Custom Forms', path: '/master-data/custom-forms' },
    ]);
    setIsShow(true);
    dispatch(fetchCustomForms());
  }, [setActivePage, setPath, setIsShow, dispatch]);

  useEffect(() => {
    if (operationSuccess) {
      setAlert({ message: formToEdit ? 'Custom Form updated successfully' : 'Custom Form created successfully', severity: 'success' });
      dispatch(fetchCustomForms());
      dispatch(clearCustomFormState());
      closeModal();
    }
    if (operationError) {
      setAlert({ message: operationError, severity: 'error' });
      dispatch(clearCustomFormState());
    }
  }, [operationSuccess, operationError, dispatch, formToEdit]);

  useEffect(() => {
    if (publishSuccess) {
      setAlert({ message: 'Form status updated successfully', severity: 'success' });
      dispatch(fetchCustomForms());
      dispatch(resetPublishCustomFormState());
      setPublishConfirmOpen(false);
      setFormToPublish(null);
    }
    if (publishError) {
      setAlert({ message: publishError, severity: 'error' });
      dispatch(resetPublishCustomFormState());
    }
  }, [publishSuccess, publishError, dispatch]);

  const closeModal = () => {
    setIsCreateModalOpen(false);
    setFormToEdit(null);
  }

  const handleEdit = (form: CustomForm) => {
    setFormToEdit(form);
    setIsCreateModalOpen(true);
  };

  const handlePublishToggle = (formId: number, currentStatus: boolean) => {
    setFormToPublish({ id: formId, status: !currentStatus });
    setPublishConfirmOpen(true);
  };

  const confirmPublish = () => {
    if (formToPublish) {
      dispatch(publishCustomForm({
        form_id: formToPublish.id,
        is_published: formToPublish.status
      }));
    }
  };

  return (
    <Box>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      <MainContainer>
        <HeaderContainer>
          <PageTitle variant="h6">Manage Custom Forms</PageTitle>
          <CreateButton variant="contained" disableElevation onClick={() => setIsCreateModalOpen(true)}>
            Create Custom Form
          </CreateButton>
        </HeaderContainer>

        <StyledTableContainer>
          <StyledTable>
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCell>Id</StyledTableCell>
                <StyledTableCell>Form Name</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
                <StyledTableCell>Order</StyledTableCell>
                <StyledTableCell>Published</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {forms && forms.length > 0 ? (
                forms.map((form: CustomForm, index: number) => (
                  <StyledTableRow key={form.form_id}>
                    <StyledTableCell>{form.form_id}</StyledTableCell>
                    <StyledTableCell>{form.form_name}</StyledTableCell>
                    <StyledTableCell>{form.form_description}</StyledTableCell>
                    <StyledTableCell>{form.order ?? '-'}</StyledTableCell>
                    <StyledTableCell>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handlePublishToggle(form.form_id!, form.is_published || false)}
                        color={form.is_published ? "error" : "primary"}
                        sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                      >
                        {form.is_published ? "Unpublish" : "Publish"}
                      </Button>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="View/Add Fields">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/master-data/custom-forms/${form.form_id}/manage-fields`)}
                            sx={{ color: '#5E8E7E' }}
                          >
                            <RemoveRedEyeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={form.is_published ? "Cannot edit published form" : "Edit Form"}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(form)}
                              color="primary"
                              disabled={form.is_published}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={6} align="center">No custom forms found.</StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>
      </MainContainer>
      <CreateCustomFormModal
        open={isCreateModalOpen}
        onClose={closeModal}
        editData={formToEdit}
      />
      <ConfirmationDialog
        open={publishConfirmOpen}
        title={formToPublish?.status ? "Publish Form" : "Unpublish Form"}
        message={`Are you sure you want to ${formToPublish?.status ? "publish" : "unpublish"} this form?`}
        onConfirm={confirmPublish}
        onClose={() => setPublishConfirmOpen(false)}
        loading={publishLoading}
      />
    </Box>
  );
};

export default CustomFormPage;
